import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { config } from './lib/supabase';
import { Bests } from './pages/Bests';
import { ExerciseDetail, Exercises } from './pages/Exercises';
import { History } from './pages/History';
import { Pair } from './pages/Pair';
import { ProgramEditor } from './pages/ProgramEditor';
import { Programs } from './pages/Programs';
import { SessionDetail } from './pages/SessionDetail';
import { Settings } from './pages/Settings';
import { SignIn } from './pages/SignIn';

function Shell({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  return (
    <div className="shell">
      <nav className="side" aria-label="Main">
        <div className="brand">GymTV</div>
        <NavLink to="/programs">Programs</NavLink>
        <NavLink to="/history">History</NavLink>
        <NavLink to="/exercises">Exercises</NavLink>
        <NavLink to="/bests">Personal bests</NavLink>
        <NavLink to="/settings">Settings</NavLink>
        <div className="spacer" />
        <div className="who">
          {session?.user.email}
          <br />
          <span className="mono">web {config.version}</span>
        </div>
      </nav>
      <main className="page">{children}</main>
    </div>
  );
}

/** Pages that need an account; anyone else sees sign-in, then returns here. */
function Private({ children, bare = false }: { children: ReactNode; bare?: boolean }) {
  const { session, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!session) return <SignIn returnTo={loc.pathname + loc.search} />;
  return bare ? <>{children}</> : <Shell>{children}</Shell>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/programs" replace />} />
      <Route path="/pair" element={<Private bare><Pair /></Private>} />
      <Route path="/programs" element={<Private><Programs /></Private>} />
      <Route path="/programs/:id" element={<Private><ProgramEditor /></Private>} />
      <Route path="/history" element={<Private><History /></Private>} />
      <Route path="/history/:id" element={<Private><SessionDetail /></Private>} />
      <Route path="/exercises" element={<Private><Exercises /></Private>} />
      <Route path="/exercises/:id" element={<Private><ExerciseDetail /></Private>} />
      <Route path="/bests" element={<Private><Bests /></Private>} />
      <Route path="/settings" element={<Private><Settings /></Private>} />
      <Route path="*" element={<Private><p>Page not found.</p></Private>} />
    </Routes>
  );
}
